import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Plus, Users, X, Trash, PencilSimple } from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';
import { validateRut, formatRut, getRutErrorMessage } from '../utils/rutValidator';

export default function Drivers() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [rutError, setRutError] = useState('');
  const [form, setForm] = useState({
    nombre: '',
    rut: '',
    telefono: '',
    licencia: '',
    empresa_id: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [driversRes, companiesRes] = await Promise.all([
        api.get('/drivers'),
        api.get('/companies')
      ]);
      setDrivers(driversRes.data);
      setCompanies(companiesRes.data);
      
      if (user?.rol !== 'super_admin' && user?.empresa_id) {
        setForm(prev => ({ ...prev, empresa_id: user.empresa_id }));
      }
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingDriver(null);
    setRutError('');
    setForm({ 
      nombre: '', 
      rut: '', 
      telefono: '', 
      licencia: '', 
      empresa_id: user?.empresa_id || '' 
    });
    setShowModal(true);
  };

  const openEditModal = (driver) => {
    setEditingDriver(driver);
    setRutError('');
    setForm({
      nombre: driver.nombre,
      rut: driver.rut,
      telefono: driver.telefono,
      licencia: driver.licencia,
      empresa_id: driver.empresa_id
    });
    setShowModal(true);
  };

  const handleRutChange = (e) => {
    const value = e.target.value;
    const formatted = formatRut(value);
    setForm({ ...form, rut: formatted });
    
    // Validar RUT
    const errorMsg = getRutErrorMessage(formatted);
    setRutError(errorMsg);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar RUT antes de enviar
    if (!validateRut(form.rut)) {
      setRutError('RUT inválido');
      toast.error('Por favor, ingresa un RUT válido');
      return;
    }
    
    try {
      const payload = { ...form };
      if (user?.rol !== 'super_admin') {
        payload.empresa_id = user.empresa_id;
      }
      
      if (editingDriver) {
        // Editar conductor existente
        await api.patch(`/drivers/${editingDriver.id}`, payload);
        toast.success('Conductor actualizado');
      } else {
        // Crear nuevo conductor
        await api.post('/drivers', payload);
        toast.success('Conductor creado');
      }
      
      setShowModal(false);
      setEditingDriver(null);
      setRutError('');
      setForm({ nombre: '', rut: '', telefono: '', licencia: '', empresa_id: user?.empresa_id || '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error ${editingDriver ? 'actualizando' : 'creando'} conductor`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este conductor?')) return;
    try {
      await api.delete(`/drivers/${id}`);
      toast.success('Conductor eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error eliminando');
    }
  };

  const getCompanyName = (id) => companies.find(c => c.id === id)?.nombre || '—';

  return (
    <div data-testid="drivers-page">
      <Toaster position="top-right" />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
            Flota · Personal
          </p>
          <h1 className="text-4xl font-bold text-neutral-900 tracking-tight">
            Conductores
          </h1>
        </div>
        <button
          onClick={openCreateModal}
          data-testid="new-driver-btn"
          className="flex items-center px-4 py-2.5 bg-blue-700 text-white rounded-md hover:bg-blue-800 text-sm font-medium"
        >
          <Plus size={18} className="mr-2" weight="bold" />
          Nuevo Conductor
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Cargando...</div>
        ) : drivers.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={48} className="mx-auto text-neutral-300 mb-3" />
            <p className="text-neutral-500">No hay conductores registrados</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="drivers-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Nombre</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">RUT</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Teléfono</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Licencia</th>
                {user?.rol === 'super_admin' && (
                  <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Empresa</th>
                )}
                <th className="text-right py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => (
                <tr key={driver.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`driver-row-${driver.id}`}>
                  <td className="py-4 px-6 font-medium text-neutral-900">{driver.nombre}</td>
                  <td className="py-4 px-6 text-neutral-700 font-mono text-xs">{driver.rut}</td>
                  <td className="py-4 px-6 text-neutral-700">{driver.telefono}</td>
                  <td className="py-4 px-6 text-neutral-700">{driver.licencia || '—'}</td>
                  {user?.rol === 'super_admin' && (
                    <td className="py-4 px-6 text-neutral-700">{getCompanyName(driver.empresa_id)}</td>
                  )}
                  <td className="py-4 px-6 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(driver)}
                        data-testid={`edit-driver-${driver.id}`}
                        className="text-blue-600 hover:bg-blue-50 p-2 rounded"
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(driver.id)}
                        data-testid={`delete-driver-${driver.id}`}
                        className="text-red-600 hover:bg-red-50 p-2 rounded"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="driver-modal">
          <div className="bg-white rounded-md max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <h2 className="text-xl font-bold text-neutral-900 tracking-tight">
                {editingDriver ? 'Editar Conductor' : 'Nuevo Conductor'}
              </h2>
              <button onClick={() => setShowModal(false)} data-testid="close-driver-modal-btn" className="p-1 hover:bg-neutral-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="driver-form">
              {/* RUT PRIMERO */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  RUT *
                </label>
                <input
                  type="text"
                  value={form.rut}
                  onChange={handleRutChange}
                  data-testid="driver-rut-input"
                  required
                  placeholder="12.345.678-9"
                  className={`w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 text-sm font-mono ${
                    rutError 
                      ? 'border-red-300 focus:ring-red-500 bg-red-50' 
                      : 'border-neutral-300 focus:ring-blue-700'
                  }`}
                />
                {rutError && (
                  <p className="mt-1 text-xs text-red-600">{rutError}</p>
                )}
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="driver-nombre-input"
                  required
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                    Teléfono *
                  </label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    data-testid="driver-telefono-input"
                    required
                    placeholder="+56912345678"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                    Licencia
                  </label>
                  <input
                    type="text"
                    value={form.licencia}
                    onChange={(e) => setForm({ ...form, licencia: e.target.value })}
                    data-testid="driver-licencia-input"
                    placeholder="A1, B, C, D, etc."
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
              </div>
              
              {user?.rol === 'super_admin' && (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                    Empresa *
                  </label>
                  <select
                    value={form.empresa_id}
                    onChange={(e) => setForm({ ...form, empresa_id: e.target.value })}
                    data-testid="driver-empresa-select"
                    required
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm bg-white"
                  >
                    <option value="">Seleccionar empresa</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  data-testid="cancel-driver-btn"
                  className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-100 text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="save-driver-btn"
                  className="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 text-sm font-medium"
                >
                  {editingDriver ? 'Actualizar' : 'Crear'} Conductor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
